import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDays, format, parse } from "date-fns";
import { Time } from "@internationalized/date";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import Loading from "@/components/Loading";
import { TimePicker } from "@/components/date-time-picker/time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ONBOARDING_FORM_STYLES } from "@/features/onboarding/components/onboarding-form-styles";
import { OnboardingStepHeader } from "@/features/onboarding/components/onboarding-step-header";

const reminderSchema = z.object({
  date: z.string().min(1, "Date is required"),
  time: z.string().min(1, "Time is required"),
});

type ReminderFormData = z.infer<typeof reminderSchema>;

export function CredentialUploadReminderForm({
  onSubmit,
}: {
  onSubmit: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [isSkipping, setIsSkipping] = useState(false);
  const today = new Date();
  const tomorrow = addDays(today, 1);
  const twoDaysFromNow = addDays(today, 2);
  const trpc = useTRPC();
  const { mutateAsync: scheduleReminder } = useMutation(
    trpc.credentialFiles.scheduleUploadReminder.mutationOptions(),
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<ReminderFormData>({
    defaultValues: {
      date: format(today, "yyyy-MM-dd"),
      time: format(new Date(), "HH:mm"),
    },
    resolver: zodResolver(reminderSchema),
  });
  const dateOptions = [
    { value: format(today, "yyyy-MM-dd"), label: "Today" },
    { value: format(tomorrow, "yyyy-MM-dd"), label: "Tomorrow" },
    {
      value: format(twoDaysFromNow, "yyyy-MM-dd"),
      label: format(twoDaysFromNow, "EEEE"),
    },
  ];

  const handleFormSubmit = async (data: ReminderFormData) => {
    try {
      setError(null);
      const [hours, minutes] = data.time.split(":");
      const reminderDate = parse(data.date, "yyyy-MM-dd", new Date());
      reminderDate.setHours(parseInt(hours));
      reminderDate.setMinutes(parseInt(minutes));

      await scheduleReminder({
        remindAt: reminderDate.toISOString(),
      });
      await onSubmit();
    } catch (err) {
      setError("Failed to set reminder. Please try again.");
    }
  };

  const [hour, minute] = watch("time").split(":");
  const time = new Time(parseInt(hour), parseInt(minute), 0, 0);
  return (
    <div>
      <OnboardingStepHeader
        title="Set an upload reminder"
        description="We'll remind you when you're ready to upload your documents. For an email with the upload link right away, set the reminder time to now."
      />
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
        <section className={ONBOARDING_FORM_STYLES.section}>
          <h3 className="mb-5 text-base font-semibold text-slate-900">
            Choose a time
          </h3>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label
                htmlFor="reminder-date"
                className={ONBOARDING_FORM_STYLES.label}
              >
                Select a Date
              </Label>
              <Select
                value={watch("date")}
                onValueChange={(value) => setValue("date", value)}
              >
                <SelectTrigger
                  id="reminder-date"
                  className={ONBOARDING_FORM_STYLES.input}
                >
                  <SelectValue placeholder="Select a day" />
                </SelectTrigger>
                <SelectContent>
                  {dateOptions.map((option) => (
                    <SelectItem
                      className="py-2.5 text-sm"
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" {...register("date")} />
              {errors.date && (
                <p role="alert" className="text-sm text-red-700">
                  {errors.date.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label
                id="reminder-time-label"
                className={ONBOARDING_FORM_STYLES.label}
              >
                Reminder Time
              </Label>
              <TimePicker
                aria-labelledby="reminder-time-label"
                className={`${ONBOARDING_FORM_STYLES.input} focus-within:border-primary focus-within:ring-primary/15 focus-within:ring-offset-0 [&>div]:text-base sm:[&>div]:text-sm`}
                value={time}
                onChange={(time) => setValue("time", time?.toString() ?? "")}
              />
              {errors.time && (
                <p role="alert" className="text-sm text-red-700">
                  {errors.time.message}
                </p>
              )}
            </div>
          </div>
        </section>
        {error && (
          <p role="alert" className={ONBOARDING_FORM_STYLES.error}>
            {error}
          </p>
        )}
        <div className={ONBOARDING_FORM_STYLES.footer}>
          <Button
            type="submit"
            className={ONBOARDING_FORM_STYLES.primaryButton}
            disabled={isSubmitting || isSkipping}
          >
            {isSubmitting ? <Loading className="w-4 h-4" /> : "Set Reminder"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className={`${ONBOARDING_FORM_STYLES.secondaryButton} sm:order-first`}
            onClick={async () => {
              setIsSkipping(true);
              setError(null);
              try {
                await onSubmit();
              } catch (err) {
                setError("Failed to skip. Please try again.");
              } finally {
                setIsSkipping(false);
              }
            }}
            disabled={isSubmitting || isSkipping}
          >
            {isSkipping ? <Loading className="w-4 h-4" /> : "Skip"}
          </Button>
        </div>
      </form>
    </div>
  );
}
